import type { Routemize_DTOs_GoogleCalendarDto } from '../../api/models/Routemize_DTOs_GoogleCalendarDto';

/**
 * Which screen the Google Calendar step should show.
 *
 * `needs-auth` and `choosing` are deliberately separate states. Signing into
 * Routemize with Google mints a Google token, so the account looks connected
 * before the user has said anything about calendars — the previous flow read
 * that single "connected" flag and skipped straight past the picker, leaving
 * people authorized but syncing nothing.
 */
export type CalendarStage = 'loading' | 'needs-auth' | 'choosing' | 'connected';

export interface CalendarConnectionState {
  isLoading: boolean;
  isAuthorized: boolean;
  syncedCalendarCount: number;
}

export function resolveCalendarStage(state: CalendarConnectionState): CalendarStage {
  if (state.isLoading) return 'loading';
  if (state.syncedCalendarCount > 0) return 'connected';
  return state.isAuthorized ? 'choosing' : 'needs-auth';
}

/**
 * The step is optional, but skipping has to be a deliberate act.
 *
 * An authorized account with nothing bound is the failure we're guarding
 * against, so that combination holds the user until they either pick a
 * calendar or say they don't want one.
 */
export function canAdvanceCalendarStep(input: {
  stage: CalendarStage;
  syncedCalendarCount: number;
  hasSkipped: boolean;
}): boolean {
  if (input.stage === 'loading') return false;
  if (input.syncedCalendarCount > 0) return true;
  return input.hasSkipped;
}

/**
 * A Google account's primary calendar id is the account's email address, which
 * is the only place the signed-in address is available to this screen.
 */
export function resolveAccountEmail(
  calendars: Routemize_DTOs_GoogleCalendarDto[]
): string | null {
  return calendars.find(c => c.isPrimary)?.id ?? null;
}

/**
 * Primary calendar first, then alphabetical. Google returns these in an order
 * that varies between accounts, and the main calendar being somewhere in the
 * middle of a long list is how people end up ticking the wrong one.
 */
export function sortCalendarsForPicker(
  calendars: Routemize_DTOs_GoogleCalendarDto[]
): Routemize_DTOs_GoogleCalendarDto[] {
  return [...calendars].sort((a, b) => {
    if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
    return (a.summary ?? a.id ?? '').localeCompare(b.summary ?? b.id ?? '');
  });
}

/**
 * Pre-tick the primary calendar. It's the right answer for nearly everyone,
 * and it means the confirm button is never dead on arrival.
 */
export function defaultSelectedCalendarIds(
  calendars: Routemize_DTOs_GoogleCalendarDto[]
): string[] {
  const primary = calendars.find(c => c.isPrimary)?.id;
  return primary ? [primary] : [];
}

export function calendarDisplayName(calendar: Routemize_DTOs_GoogleCalendarDto): string {
  return calendar.summary?.trim() || calendar.id || 'Untitled calendar';
}
